import {
  listLocalOrdersForShift,
  putCompactedLocalOrder,
  purgeLocalOrderAuxiliaryData,
} from "./db";
import { LOCAL_COMPACTION } from "./offline-sync-policy";
import type { LocalOrderRecord } from "./types";

export type LocalCompactionResult = {
  scanned: number;
  eligible: number;
  compacted: number;
  skipped: number;
  operationsDeleted: number;
  kitchenTicketsDeleted: number;
  bytesEstimateFreed: number;
  dryRun: boolean;
  ranAt: string;
};

export type LocalCompactionStats = {
  totalOrders: number;
  syncedOrders: number;
  compactedOrders: number;
  eligibleToCompact: number;
  pendingSync: number;
};

export function isLocalOrderCompactionEligible(
  order: LocalOrderRecord,
  nowMs: number = Date.now(),
  minAgeMs: number = LOCAL_COMPACTION.minAgeMs,
): boolean {
  if (order.isCompacted) return false;
  if (order.syncStatus !== "synced" || !order.serverOrderId) return false;
  const anchor = order.compactedAt ?? order.updatedAt ?? order.completedAt;
  if (!anchor) return false;
  const ageMs = nowMs - new Date(anchor).getTime();
  return Number.isFinite(ageMs) && ageMs >= minAgeMs;
}

export function buildCompactedLocalOrder(
  order: LocalOrderRecord,
  compactedAt: string = new Date().toISOString(),
): LocalOrderRecord {
  return {
    ...order,
    isCompacted: true,
    compactedAt,
    cartItems: [],
    isDelivery: order.isDelivery,
    deliveryData: undefined,
    couponCode: undefined,
    couponApplyToCartIndices: undefined,
    couponApplications: undefined,
    couponSnapshots: undefined,
    voucherSnapshots: undefined,
    rewardSnapshot: undefined,
    rewardId: undefined,
    discountBreakdown: undefined,
    pricingSource: undefined,
    memberReceiptSnapshot: undefined,
    voucherCodes: undefined,
    paymentCouponCode: undefined,
    couponOrderItemIds: undefined,
    paymentCouponCartIndices: undefined,
    paymentCouponApplications: undefined,
    lastSyncError: undefined,
    updatedAt: compactedAt,
  };
}

function estimateRecordBytes(record: unknown): number {
  try {
    return new Blob([JSON.stringify(record)]).size;
  } catch {
    return 0;
  }
}

export async function getLocalCompactionStats(
  shiftId: string,
  minAgeMs: number = LOCAL_COMPACTION.minAgeMs,
): Promise<LocalCompactionStats> {
  const orders = await listLocalOrdersForShift(shiftId);
  const nowMs = Date.now();
  let syncedOrders = 0;
  let compactedOrders = 0;
  let eligibleToCompact = 0;
  let pendingSync = 0;

  for (const order of orders) {
    if (order.syncStatus === "synced") syncedOrders += 1;
    if (order.isCompacted) compactedOrders += 1;
    if (isLocalOrderCompactionEligible(order, nowMs, minAgeMs)) {
      eligibleToCompact += 1;
    }
    if (
      ["queued", "syncing", "failed", "needs_review"].includes(order.syncStatus)
    ) {
      pendingSync += 1;
    }
  }

  return {
    totalOrders: orders.length,
    syncedOrders,
    compactedOrders,
    eligibleToCompact,
    pendingSync,
  };
}

function isIndexedDbAvailable(): boolean {
  return typeof globalThis.indexedDB !== "undefined";
}

export async function compactSyncedLocalOrdersForShift(
  shiftId: string,
  options: {
    minAgeMs?: number;
    dryRun?: boolean;
    deleteSyncedOperations?: boolean;
    deleteKitchenTickets?: boolean;
  } = {},
): Promise<LocalCompactionResult> {
  if (!isIndexedDbAvailable()) {
    return {
      scanned: 0,
      eligible: 0,
      compacted: 0,
      skipped: 0,
      operationsDeleted: 0,
      kitchenTicketsDeleted: 0,
      bytesEstimateFreed: 0,
      dryRun: options.dryRun ?? false,
      ranAt: new Date().toISOString(),
    };
  }

  const minAgeMs = options.minAgeMs ?? LOCAL_COMPACTION.minAgeMs;
  const dryRun = options.dryRun ?? false;
  const deleteSyncedOperations =
    options.deleteSyncedOperations ?? LOCAL_COMPACTION.deleteSyncedOperations;
  const deleteKitchenTickets =
    options.deleteKitchenTickets ?? LOCAL_COMPACTION.deleteKitchenTickets;

  const orders = await listLocalOrdersForShift(shiftId);
  const nowMs = Date.now();
  const ranAt = new Date(nowMs).toISOString();

  let eligible = 0;
  let compacted = 0;
  let skipped = 0;
  let operationsDeleted = 0;
  let kitchenTicketsDeleted = 0;
  let bytesEstimateFreed = 0;

  for (const order of orders) {
    if (!isLocalOrderCompactionEligible(order, nowMs, minAgeMs)) {
      if (
        order.syncStatus === "synced" &&
        order.serverOrderId &&
        !order.isCompacted
      ) {
        skipped += 1;
      }
      continue;
    }
    eligible += 1;

    const beforeBytes = estimateRecordBytes(order);
    const compactedRecord = buildCompactedLocalOrder(order, ranAt);
    const afterBytes = estimateRecordBytes(compactedRecord);
    bytesEstimateFreed += Math.max(0, beforeBytes - afterBytes);

    if (dryRun) {
      compacted += 1;
      continue;
    }

    await putCompactedLocalOrder(compactedRecord);
    const purged = await purgeLocalOrderAuxiliaryData(order.clientOrderId, {
      deleteSyncedOperations,
      deleteKitchenTickets,
    });
    operationsDeleted += purged.operationsDeleted;
    kitchenTicketsDeleted += purged.kitchenTicketsDeleted;
    compacted += 1;
  }

  return {
    scanned: orders.length,
    eligible,
    compacted,
    skipped,
    operationsDeleted,
    kitchenTicketsDeleted,
    bytesEstimateFreed,
    dryRun,
    ranAt,
  };
}

export async function runLocalCompactionAfterAutoSync(
  shiftId: string,
  syncedCount: number,
): Promise<LocalCompactionResult | null> {
  if (!LOCAL_COMPACTION.enabled || !LOCAL_COMPACTION.runAfterAutoSync) {
    return null;
  }
  if (syncedCount <= 0) return null;
  try {
    return await compactSyncedLocalOrdersForShift(shiftId);
  } catch (error) {
    console.warn("[local-compaction] post auto-sync failed", error);
    return null;
  }
}

export function formatCompactionBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
